import { Amplify, Auth } from 'aws-amplify';
import React from 'react';
import { Link } from 'react-router-dom';

import type { KeyboardEventKey } from 'keyboard-event-key-type';
import type { ChangeEventHandler } from 'react';
import type { PTUserInfo } from 'Lib/PTUserInfo.type';
import type { ErrorResponse } from 'utils/okta/oktaInviteService';
import type { WithRouterAddedProps } from 'Lib/withRouter';
import type { Untrusted } from 'utils/types';
import type { AttachedCompanies } from 'Backend/users/utils/attachedCompanies/attachedCompanies.types';

import XeroLogo from 'Components/Integrations/Xero/XeroLogo.svg';
import InputError from 'Components/UI_Library/FormElements/InputError';
import { ReactComponent as FaceID } from 'Components/UI_Library/Icons/FaceID.svg';
import Notification from 'Components/UI_Library/Notification/Notification';
import * as MESSAGES from 'Lib/Messages';
import { setCurrentCompanyId, setCurrentUserInfo } from 'Lib/Utils';
import Validator from 'Lib/Validator';
import { withRouter } from 'Lib/withRouter';
import AuthPageWrapper from 'Components/Auth/AuthPageWrapper/AuthPageWrapper';
import Button from 'Components/UI_Library/FormElements/Button';
import TextInput from 'Components/UI_Library/FormElements/TextInput';
import { registerNotifications } from 'mobile-app/pushNotifications';
import { getBiometricsType } from 'mobile-app/biometrics';
import { API as api } from 'Backend';
import classes from './SignIn.module.css';
import AvatarUser from 'Components/UI_Library/AvatarUser';
import { signUpCodeNumberOfCharacters } from 'Components/SignIn/utils/signUpCodeNumberOfCharacters';
import { parsePastedSignUpCode } from './utils/parsePastedSignUpCode';
import { OktaExternalAuth } from 'utils/okta/OktaExternalAuthService';
import { OktaInternalAuth } from 'utils/okta/OktaInternalAuthService';
import Tooltip from 'Components/UI_Library/Tooltip/Tooltip';
import { passwordTooltipText } from 'Components/SignIn/utils/passwordTooltipText';
import { activateUser, setPassword, isError } from 'utils/okta/oktaInviteService';
import { setAttachedCompanies } from 'Backend/users/utils/attachedCompanies';
import { logger } from 'utils/logger';
import awsExports from '../../aws-exports';
import { BiometricsService } from 'mobile-app/biometricsService';

let _hsq: (['identify', { email?: string | null }] | ['setPath', '/signin'] | ['trackPageView'])[];
declare global {
    interface Window {
        /** It seems like this is something related to hubspot that is saved in global window object by {@link UserSignIn} component. */
        _hsq?: typeof _hsq;
    }
}

type ActivateAndSetPasswordInput = {
    sessionToken: string;
};

type Props = WithRouterAddedProps;
type State = {
    user: {
        code?: string;
        email?: string | null;
        password?: string;
        new_password?: string;
        confirm_password?: string;
    };
    loginState?: 'entry' | 'default';
    loading: boolean;
    isConfirmedUser: boolean;
    isShowMFACode: string;
    challengeName: 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA' | 'NEW_PASSWORD_REQUIRED' | '' | null | undefined;
    errors: { [key: string]: string };
    isError: boolean;
    errorMessage: string | null;
    isShowActiveLink: boolean;
    isShowPassword: boolean;
    biometrics: boolean;
    biometricOption: string | null;
    activationToken: string | null;
    activate?: string | null;
    newUser?: string | null;
    inviterCompany?: unknown;
    signUpCode?: string[];
    AuthUser?: unknown;
};

class UserSignIn extends React.Component<Props, State> {
    signUpCodeRef: React.RefObject<HTMLInputElement>;

    constructor(props: Props) {
        super(props);
        this.state = {
            user: {},
            loading: false,
            isConfirmedUser: true,
            isShowMFACode: '',
            challengeName: '',
            errors: {},
            isError: false,
            errorMessage: null,
            isShowActiveLink: false,
            isShowPassword: false,
            biometrics: false,
            biometricOption: '',
            activationToken: '',
        };

        this.state = { ...this.getInitialState(), ...this.state };

        // Bypassing invites feature
        this.signUpCodeRef = React.createRef();
    }

    /* eslint-disable-next-line react/sort-comp */
    biometricLogin = async () => {
        const biometricsService = await BiometricsService.getBiometrics();
        const credentials = await biometricsService.getCredentials();
        if (!credentials) {
            logger.error('No biometric credentials found');
            return;
        }
        const user = { email: credentials.username, password: credentials.password };
        try {
            const response = await api.checkEmail({ email: user.email });
            switch (response.data?.userType) {
                case 'oktaInternal':
                    const oktaInternalInstance = await OktaInternalAuth.getInstance();
                    await oktaInternalInstance.signIn();
                    break;
                case 'oktaExternal':
                    const oktaExternalInstance = await OktaExternalAuth.getInstance();
                    await oktaExternalInstance.signIn();
                    break;
                default:
                    if (user.password) {
                        this.setState({ loginState: 'default', user }, () => this.handleLogin());
                    } else {
                        this.setState({ loginState: 'default', user });
                    }
                    break;
            }
        } catch (err) {
            const errorMessage = 'Error redirecting user, please try again or contact support';
            this.setState({ user, errorMessage, isError: true, loading: false });
        }
    };

    setBiometricsState = async (source?: string | null) => {
        const biometrics = await getBiometricsType();

        if (biometrics?.biometricsAvailable && biometrics?.biometricOption === 'Face ID' && source !== 'signout') {
            this.biometricLogin();
        }
        this.setState({
            biometrics: biometrics?.biometricsAvailable,
            biometricOption: biometrics?.biometricOption,
        });
    };

    getInitialState() {
        return {
            AuthUser: {},
            user: {
                name: '',
                email: '',
                code: '',
                password: '',
                new_password: '',
                confirm_password: '',
            },
            loginState: 'entry' as const,
        };
    }

    async componentDidMount() {
        const view = localStorage.getItem('paymentsView');
        sessionStorage.clear();
        localStorage.clear();
        localStorage.setItem('paymentsView', view!); // FIXME: Should not use localStorage directly, because of lack of typing, should abstract into typechecked getter and setter.
        const s = document.createElement('script');
        s.type = 'text/javascript';
        s.async = true;
        s.src = 'https://edge.xero.com/platform/sso/xero-sso.js';
        document.body.appendChild(s);

        const queryParams = new URLSearchParams(window.location.search);
        const redirectError = queryParams.get('error');
        const activationToken = queryParams.get('activationToken');
        const email = queryParams.get('email');
        const activate = queryParams.get('activate');
        const newUser = queryParams.get('newUser');
        const companyName = queryParams.get('companyName');
        const biometricState = queryParams.get('source');

        if (redirectError) {
            this.setState({
                errorMessage: redirectError,
                isError: true,
            });
        }

        if (activationToken && email) {
            this.setState((state) => ({
                user: {
                    ...state.user,
                    email: decodeURIComponent(email),
                },
                activationToken: decodeURIComponent(activationToken),
                challengeName: 'NEW_PASSWORD_REQUIRED',
            }));
        }

        // Bypassing invites: If user is activating themselves in a company
        this.setState({
            activate, // This parameter is for an existing or new invited user
            newUser, // These parameters are for a new user
            inviterCompany: companyName, // These parameters are for a new user
        });

        // Bypassing invites: If the user has copied the sign up code to their clipboard, we add it in automatically
        // We ignore this for okta invite since we don't use codes to activate account
        if (newUser && !activationToken) {
            const clipboardContents = await navigator.clipboard.readText();
            const signUpCode = parsePastedSignUpCode(clipboardContents); // FIXME: I think we should just make this a string instead of an array of strings.
            if (signUpCode?.length === signUpCodeNumberOfCharacters) {
                this.setState({ signUpCode });
            }
        }

        this.setBiometricsState(biometricState).catch((error /* INSPECT: Should we await this? */) =>
            logger.error('Failed to set biometrics state: ', { error })
        );
        window.addEventListener('keypress', this.onEnter, false);
    }

    componentDidUpdate(_prevProps: Props, prevState: State) {
        const signUpCodeChanged = this.state.signUpCode !== prevState.signUpCode;
        // Bypassing invites: Update the password field if the user is changing the signUpCode
        if (this.state.newUser && signUpCodeChanged) {
            // We populate the password field with the signUpCode
            this.setState((prevState_) => ({
                user: { ...prevState_.user, password: prevState_.signUpCode?.join('') },
            }));
        }
    }

    componentWillUnmount() {
        window.removeEventListener('keypress', this.onEnter, false);
    }

    onEnter = (e: KeyboardEvent) => {
        const { challengeName } = this.state;
        if (e.key === 'Enter') {
            e.preventDefault();
            if (this.state.loading) return;
            if (
                challengeName &&
                ['SMS_MFA', 'SOFTWARE_TOKEN_MFA'].includes(challengeName) &&
                this.state.user?.code &&
                isConfirmSignMfaType(challengeName)
            ) {
                this.handleVerifyCode(undefined, challengeName);
            } else if (
                this.state?.user?.email &&
                this.state?.user?.password &&
                !this.state?.challengeName &&
                this.state?.loginState !== 'entry'
            ) {
                this.handleLogin();
            } else if (this.state?.user?.email && !this.state?.challengeName && this.state?.loginState === 'entry') {
                this.handleButtonClick();
            }
        }
    };

    handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        const {
            target: { name, value },
        } = event;
        const { user, errors, challengeName } = this.state;
        user[name as keyof typeof user] = value;
        if (errors[name]) {
            delete errors[name];
        }
        if (name === 'code' && typeof value === 'string' && value.length === 6 && isConfirmSignMfaType(challengeName)) {
            this.setState({ user, errors }, () => this.handleVerifyCode(undefined, challengeName));
        } else {
            this.setState({ user, errors });
        }
    };

    clearForm = () => {
        this.setState(this.getInitialState());
    };

    clearError = () => {
        this.setState({ isError: false, errorMessage: null });
    };

    activateAndSetPassword = async (
        activationToken: string,
        newPassword: string
    ): Promise<ActivateAndSetPasswordInput | ErrorResponse> => {
        const activateUserResponse = await activateUser(activationToken);

        if (isError(activateUserResponse)) {
            return activateUserResponse;
        }
        const setPasswordResponse = await setPassword({
            stateToken: activateUserResponse.stateToken,
            password: newPassword,
        });

        if (isError(setPasswordResponse)) {
            return setPasswordResponse;
        }

        return {
            sessionToken: setPasswordResponse.sessionToken,
        };
    };

    checkOFXTermsAcceptance = (attachedCompanies: AttachedCompanies) =>
        attachedCompanies.find((company) => company.type === 'Owner' && !company.acceptedMigrationTerms);

    setAuthData = async (signInUserSession: { idToken: { payload: { sub: string; name: string; email: string } } }) => {
        const { payload } = signInUserSession.idToken;
        const previousPathName = this.props?.location?.state?.from?.pathname;
        const previousRoute = previousPathName && previousPathName !== '/switch' ? previousPathName : '/';

        // Check if path has a redirect
        const searchParams = new URLSearchParams(window.location.search);
        const redirectParam = searchParams.get('redirect');
        const redirectDecoded = redirectParam ? decodeURIComponent(redirectParam) : '';
        // Check if redirect has a companyid= passed in
        const hasCompanyIdInRedirect = redirectDecoded.includes('companyid=');
        const companyIdFromRedirect = hasCompanyIdInRedirect ? redirectDecoded.split('companyid=')?.[1] : '';

        console.log('On sign-in, got props: ', this.props);
        console.log('On sign-in, got user session: ', signInUserSession);

        const userInfo: PTUserInfo = {
            sub: payload.sub,
            name: payload.name,
            email: payload.email,
        };

        setCurrentUserInfo(userInfo);

        const attachedCompanies = await api.getAttachedCompanies();
        console.log('attachedCompanies', JSON.stringify(attachedCompanies));
        const Permissions = attachedCompanies?.data;

        // Pending invites
        const pendingInvites = (await api.getAttachedCompanies('PENDING'))?.data || [];
        console.log('pendingInvites', pendingInvites);

        // request for push notifications
        console.log('registerNotifications');
        await registerNotifications();

        setAttachedCompanies(Permissions || []);

        console.log('Permissions on login: ', JSON.stringify(Permissions || []));
        if (!attachedCompanies.success) {
            this.setState({
                isError: true,
                errorMessage: 'Your permissions appear to be corrupted. Please clear your cache and retry.',
                loading: false,
            });
            return;
        }

        setCurrentCompanyId(Permissions?.[0]?.companyId || '');

        // Bypassing invites: Redirect new user to user-setup page
        if (this.state.newUser) {
            this.props.history('/user-setup?newUser=true');
            return;
        }

        // If there are no found companies, redirect to /welcome
        if (Permissions?.length === 0) {
            this.props.history('/welcome');
            return;
        }
        if (Permissions && this.checkOFXTermsAcceptance(Permissions)) {
            this.props.history('/ofx-migration');
            return;
        }

        // If we are navigating to a redirect, check if companyid is passed in the URL, if so redirect to that company
        const companyInAttachedCompanies = attachedCompanies?.data?.find(
            (company) => company?.companyId === companyIdFromRedirect
        );
        if (companyIdFromRedirect && companyInAttachedCompanies) {
            setCurrentCompanyId(companyIdFromRedirect);
            this.redirectFromAuth(companyInAttachedCompanies.companyStatus, redirectDecoded || previousRoute);
            return;
        }

        if ((Permissions?.length ?? 0) >= 2 || pendingInvites?.length) {
            // Redirect to switch companies form if there are multiple companies, or if the user has pending invites
            this.props.history({
                pathname: '/switch',
                search: redirectParam ? `?redirect=${redirectParam}` : '',
            });
            return;
        }

        // Otherwise redirect to appropriate route
        this.redirectFromAuth(Permissions?.[0]?.companyStatus, redirectDecoded || previousRoute);
    };

    redirectFromAuth = (companyStatus: string | undefined, route: string) => {
        switch (companyStatus) {
            case 'APPROVED - CARDS ONLY':
            case 'APPROVED':
                this.props.history(route);
                break;
            case 'FAILED':
                this.props.history('/signout');
                break;
            default:
                this.props.history('/onboarding');
                break;
        }
    };

    handleShowPassword = (flag: boolean) => {
        this.setState({ isShowPassword: flag });
    };

    handleVerifyCode = async (
        e: React.MouseEvent | undefined,
        challengeName: 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA' | null | undefined
    ) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }

        if (!this.isValidation()) {
            return false;
        }

        const {
            AuthUser,
            user: { code },
        } = this.state;
        const mfaType = challengeName;

        this.setState({ loading: true });
        try {
            const user = await Auth.confirmSignIn(
                AuthUser, // Return object from Auth.signIn()
                code!, // Confirmation code // FIXME: Just asserting non-null as existing runtime behavior
                mfaType // MFA Type e.g. SMS_MFA, SOFTWARE_TOKEN_MFA
            );
            const { signInUserSession } = user;
            const {
                user: { email, password },
            } = this.state;
            console.log('registerBiometrics');
            const biometricsService = await BiometricsService.getBiometrics();
            await biometricsService.registerCredentials({ username: email ?? undefined, password });
            this.setAuthData(signInUserSession);
        } catch (err) {
            this.setState({
                errorMessage: 'The code is invalid.',
                isError: true,
                loading: false,
            });
        }
        return true;
    };

    handleLogin = async (e?: React.MouseEvent, loginFromUpdatePassword?: boolean) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        if (!this.isValidation()) {
            return false;
        }
        Amplify.configure(awsExports);

        // Bypassing invites: For when a new user has successfully updated their password and are signing in
        const newUserLoggingIn = this.state.newUser && loginFromUpdatePassword;

        this.setState({ loading: true });

        const {
            user: { email, password },
        } = this.state;
        try {
            const user = await Auth.signIn(email!, password);

            const { challengeName, signInUserSession } = user;

            // Bypassing invites: For when a new user has successfully updated their password, we keep the same NEW_PASSWORD_REQUIRED screen active, as they are redirected from this screen
            if (newUserLoggingIn) {
                this.setState({ challengeName: 'NEW_PASSWORD_REQUIRED', AuthUser: user });
            } else {
                this.setState({ challengeName, AuthUser: user });
            }

            // Hubspot related code
            _hsq = window._hsq = window._hsq || []; /* eslint-disable-line no-multi-assign */

            _hsq.push([
                'identify',
                {
                    email,
                },
            ]);
            _hsq.push(['setPath', '/signin']);
            _hsq.push(['trackPageView']);

            switch (challengeName) {
                case 'SMS_MFA':
                case 'SOFTWARE_TOKEN_MFA':
                case 'NEW_PASSWORD_REQUIRED':
                    // Bypassing invites: For when a new user has successfully updated their password, we set the auth data
                    if (newUserLoggingIn) {
                        const biometricsService = await BiometricsService.getBiometrics();
                        await biometricsService.registerCredentials({ username: email ?? undefined, password });
                        this.setAuthData(signInUserSession);
                    }

                    this.setState({
                        loading: false,
                    });
                    break;
                default:
                    console.log('registerBiometrics');
                    const biometricsService = await BiometricsService.getBiometrics();
                    await biometricsService.registerCredentials({ username: email ?? undefined, password });
                    this.setAuthData(signInUserSession);
                    break;
            }
        } catch (err) {
            let errorMessage = '';
            if (err instanceof Error && err.message) {
                errorMessage = err.message;
                console.log('errorMessage', errorMessage);
                errorMessage = errorMessage.replace('User is not confirmed', 'User is not verified');
            }

            if (hasCodeProperty(err) && err.code === 'UserNotConfirmedException') {
                if (!this.state.user.email) {
                    logger.error('User email is not set', { function: this.handleLogin.name, user: this.state.user });
                } else {
                    localStorage.setItem('UserEmail', this.state.user.email);
                }
                this.setState({ isShowActiveLink: true });
            }
            if (errorMessage === 'Password reset required for the user') {
                this.props.history('/update-pwd');
            }
            this.setState({ errorMessage, isError: true, loading: false });
        }

        return true;
    };

    handleOktaExternalSignIn = async (e: React.MouseEvent | undefined) => {
        e?.preventDefault();
        const instance = await OktaExternalAuth.getInstance();
        await instance.signIn();
    };

    handleUpdatePassword = async (e?: React.MouseEvent) => {
        e?.stopPropagation();
        e?.preventDefault();

        if (!this.isValidation()) {
            return false;
        }

        this.setState({ loading: true });
        const { user, activationToken } = this.state;

        if (activationToken) {
            const result = await this.activateAndSetPassword(activationToken, user.new_password ?? '');
            if (isError(result)) {
                return this.setState({
                    isError: true,
                    errorMessage: 'An error has occurred creating your account. Please try again',
                    loading: false,
                });
            }

            const authService = await OktaExternalAuth.getInstance();
            await authService.signIn({
                sessionToken: result.sessionToken,
            });
            return;
        }

        Auth.signIn(user.email!.toLowerCase(), user.password)
            .then((res) => {
                Auth.completeNewPassword(res, user.new_password!)
                    .then(() => {
                        const view = localStorage.getItem('paymentsView');
                        sessionStorage.clear();
                        localStorage.clear();
                        localStorage.setItem('paymentsView', view!);
                        this.setState({
                            errorMessage: MESSAGES.SUCCESS_RESET_PASSWORD,
                            isError: false,
                            loading: false,
                        });
                        // Bypassing invites: If a new user successfully reset their password, we automatically log them in
                        if (this.state.newUser) {
                            this.handleNewUserLogin();
                        } else {
                            this.clearForm();
                            this.setState({
                                challengeName: '',
                            });
                        }
                        return false;
                    })
                    .catch((err) => {
                        this.setState({ loading: false });
                        if (err.message) {
                            this.setState({ errorMessage: err.message, isError: true });
                        }
                    });
            })
            .catch((err) => {
                let errorMessage = '';
                if (err.message) {
                    errorMessage = err.message;
                }

                if (err.code === 'UserNotConfirmedException') {
                    this.setState({ isShowActiveLink: true });
                }

                this.setState({ errorMessage, isError: true, loading: false });
            });

        return true;
    };

    isValidation() {
        this.setState({ errors: {}, errorMessage: '' });
        const { user, challengeName, loginState } = this.state;
        const bypassingInvitesNewUser = this.state.newUser;

        const errors: Record<string, string> = {};
        let isError = false;
        const validator = new Validator();

        if (!challengeName && !bypassingInvitesNewUser && loginState !== 'entry') {
            if (!validator.required(user.email)) {
                errors.email = MESSAGES.ERROR_REQUIRED;
                isError = true;
            } else if (!validator.email(user.email.trim())) {
                errors.email = MESSAGES.ERROR_EMAIL;
                isError = true;
            }
            if (!validator.required(user.password)) {
                errors.password = MESSAGES.ERROR_REQUIRED;
                isError = true;
            }
        } else if (!challengeName && !bypassingInvitesNewUser && loginState === 'entry') {
            if (!validator.required(user.email)) {
                errors.email = MESSAGES.ERROR_REQUIRED;
                isError = true;
            } else if (!validator.email(user.email.trim())) {
                errors.email = MESSAGES.ERROR_EMAIL;
                isError = true;
            }
        } else if (challengeName && ['SMS_MFA', 'SOFTWARE_TOKEN_MFA'].includes(challengeName)) {
            if (!validator.required(user.code)) {
                errors.code = MESSAGES.ERROR_REQUIRED;
                isError = true;
            }
        } else if (challengeName === 'NEW_PASSWORD_REQUIRED') {
            if (!validator.required(user.new_password)) {
                errors.new_password = MESSAGES.ERROR_REQUIRED;
                isError = true;
            } else if (!validator.minLength(user.new_password, 8)) {
                errors.new_password = MESSAGES.ERROR_MINLENGTH;
                isError = true;
            } else if (!validator.required(user.confirm_password)) {
                errors.confirm_password = MESSAGES.ERROR_REQUIRED;
                isError = true;
            } else if (!validator.match(user.new_password, user.confirm_password)) {
                errors.confirm_password = MESSAGES.ERROR_CONFIRM_PASS;
                isError = true;
            }
        }

        // Bypassing invites: Validates a new user signing in for the first time (not run this code if they are on update password form)
        if (bypassingInvitesNewUser && challengeName !== 'NEW_PASSWORD_REQUIRED') {
            if (!validator.required(user.email)) {
                errors.email = MESSAGES.ERROR_REQUIRED;
                isError = true;
            } else if (!validator.email(user.email.trim())) {
                errors.email = MESSAGES.ERROR_EMAIL;
                isError = true;
            }
            // If the sign up code does not have all the characters
            const { signUpCode } = this.state;
            if (signUpCode?.length !== signUpCodeNumberOfCharacters) {
                errors.signUpCode = `Please fill in all ${signUpCodeNumberOfCharacters} characters`;
                isError = true;
            }
        }

        this.setState({ errors });
        if (isError) {
            return false;
        }

        return true;
    }

    renderInviterCompany = () => {
        const parsed = new URLSearchParams(window.location.search);
        const inviterCompany = parsed.get('companyName');
        if (inviterCompany) {
            return (
                <div className={classes.inviterCompany}>
                    <AvatarUser
                        size="32"
                        user={{
                            fullName: inviterCompany,
                            email: parsed.get('companyWebsite') ?? undefined, // INSPECT: What is going on here? Have we hacked avatar component to show company logo?
                            id: inviterCompany /* HACK: Avatar uses id in a useEffect. */,
                        }}
                        loaded
                    />
                </div>
            );
        }
    };

    handleSignUpCodeChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const { signUpCode = [] } = this.state;
        signUpCode[index] = event.target.value;
        this.setState({ signUpCode: [...signUpCode] });
    };

    handleSignUpCodeKeyUp = (event: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        const key = event.key as KeyboardEventKey;

        const previousInputBox = this.signUpCodeRef?.current?.children[index > 0 ? index - 1 : index];
        const nextInputBox =
            this.signUpCodeRef?.current?.children[index < signUpCodeNumberOfCharacters - 1 ? index + 1 : index];
        if (!(previousInputBox instanceof HTMLInputElement)) {
            const errorMessage = 'Previous element is not an input element';
            logger.error(errorMessage, { index, function: this.handleSignUpCodeKeyUp.name });
            throw new Error(errorMessage);
        }
        if (!(nextInputBox instanceof HTMLInputElement)) {
            const errorMessage = 'Next input is not focusable';
            logger.error(errorMessage, { index, function: this.handleSignUpCodeKeyUp.name });
            throw new Error(errorMessage);
        }

        if (key === 'Backspace' || key === 'ArrowLeft') {
            if (key === 'Backspace') {
                // Make sure that the current input is deleted on backspace in case onChange didn't trigger (eg if cursor was before the value). It's edge case since we try to keep the input selected at all times.
                const { signUpCode = [] } = this.state;
                signUpCode[index] = '';
                this.setState({ signUpCode: [...signUpCode] });
            }
            previousInputBox.focus();
            previousInputBox.setSelectionRange(0, 1);
            return;
        }

        const signUpCodeUpdated = key === event.currentTarget.value; // Pressed the same key as the current value. We handle this here instead of in onChange to trigger in the case that the user "changed" the value to the same value, in which case this triggers but not onChange.
        if ((key === 'Enter' && event.currentTarget.value) || key === 'ArrowRight' || signUpCodeUpdated) {
            nextInputBox.focus();
            nextInputBox.setSelectionRange(0, 1);
            return;
        }
        event.currentTarget.setSelectionRange(0, 1); // Rreselect the current input to allow changing it.
    };

    handleSignUpCodePaste = (event: React.ClipboardEvent<HTMLInputElement>, index: number) => {
        const signUpCode = [...(this.state.signUpCode || [])];
        const pastedData = event.clipboardData.getData('Text');
        if (pastedData) {
            const characters = parsePastedSignUpCode(pastedData).splice(index, signUpCodeNumberOfCharacters);
            characters.forEach((char, charIndex) => {
                signUpCode[charIndex + index] = char;
            });
            this.setState({ signUpCode });
        }
    };

    renderSignUpCodeField = () => {
        const { signUpCode, errors } = this.state;
        const inputBoxes = Array.from(Array(signUpCodeNumberOfCharacters).keys());
        return (
            <div className={`form-group ${classes.signUpCodeSection}`}>
                <label htmlFor="sign-up-code-0">Sign up code</label>
                <div className={classes.signUpCodeField} ref={this.signUpCodeRef}>
                    {inputBoxes.map((index) => (
                        <input
                            key={`sign-up-code-${index}`}
                            id={`sign-up-code-${index}`}
                            style={{ width: `calc(100% / ${signUpCodeNumberOfCharacters})` }}
                            value={signUpCode?.[index] || ''}
                            onFocus={(event) => event?.currentTarget.setSelectionRange(0, 1)}
                            onClick={(event) => event?.currentTarget.setSelectionRange(0, 1)}
                            onChange={(event) => this.handleSignUpCodeChange(event, index)}
                            onKeyUp={(event) => this.handleSignUpCodeKeyUp(event, index)}
                            onPaste={(event) => this.handleSignUpCodePaste(event, index)}
                            maxLength={1}
                            autoComplete="off"
                        />
                    ))}
                    <InputError error={errors.signUpCode} />
                </div>
            </div>
        );
    };

    handleNewUserLogin = () => {
        const loginFromUpdatePassword = true;
        this.setState(
            (prevState) => ({
                user: { ...prevState.user, password: prevState.user.new_password },
            }),
            () => this.handleLogin(undefined, loginFromUpdatePassword)
        );
    };

    handleButtonClick = async (e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        if (!this.isValidation()) {
            return false;
        }

        this.setState({ loading: true });

        const {
            user: { email, password },
            loginState,
        } = this.state;
        switch (loginState) {
            case 'entry':
                try {
                    const response = await api.checkEmail({ email });
                    switch (response.data?.userType) {
                        case 'oktaInternal':
                            const oktaInternalInstance = await OktaInternalAuth.getInstance();
                            await oktaInternalInstance.signIn();
                            break;
                        case 'oktaExternal':
                            const oktaExternalInstance = await OktaExternalAuth.getInstance();
                            await oktaExternalInstance.signIn();
                            break;
                        default:
                            this.setState({ loginState: 'default' });
                            if (password) {
                                await this.handleLogin();
                            }
                            this.setState({ loading: false });
                            break;
                    }
                } catch (err) {
                    const errorMessage = 'Error redirecting user, please try again or contact support';
                    this.setState({ errorMessage, isError: true, loading: false });
                }
                break;
            default:
                await this.handleLogin();
                break;
        }
    };

    render() {
        const {
            isShowActiveLink,
            user,
            challengeName,
            loading,
            activationToken,
            errors,
            isError,
            errorMessage,
            isShowPassword,
            biometrics,
            biometricOption,
        } = this.state;

        const { location } = this.props;
        const okta = new URLSearchParams(location.search).has('okta-test');

        let formTitle = 'Welcome back to Paytron';
        let formText = '';
        if (challengeName === 'SMS_MFA') {
            formTitle = 'Enter your SMS code';
        } else if (challengeName === 'SOFTWARE_TOKEN_MFA') {
            formTitle = 'Enter your auth code';
        } else if (challengeName === 'NEW_PASSWORD_REQUIRED') {
            formTitle = 'Choose new password';
        }

        // Bypassing invites feature - for a brand new user
        if (this.state.activate && this.state.newUser && this.state.inviterCompany) {
            formTitle = 'Welcome to Paytron!';
            formText = `To accept your invite from ${this.state.inviterCompany} and set up your profile, please start by entering your sign up code`;

            if (challengeName === 'NEW_PASSWORD_REQUIRED') {
                formTitle = 'Set a password';
                formText = activationToken
                    ? 'Set a password to continue'
                    : "It's free to sign up and get started with Paytron";
            }
        }

        return (
            <AuthPageWrapper>
                <div className={classes.form}>
                    <div className={classes.title}>
                        <h2>{formTitle}</h2>
                        {formText && <p>{formText}</p>}
                        {this.state.activate &&
                            (challengeName !== 'NEW_PASSWORD_REQUIRED' || !!activationToken) &&
                            this.renderInviterCompany()}
                    </div>
                    <div className={classes.body}>
                        <form method="POST">
                            <Notification
                                isError={isError}
                                message={errorMessage}
                                handleCloseMsg={() => this.clearError()}
                            />
                            {isShowActiveLink ? (
                                <div className="link-active">
                                    {`Please click `}
                                    <Link to="/verify-email?re_active">here</Link>
                                    {` to activate your account.`}
                                </div>
                            ) : null}

                            {(() => {
                                switch (challengeName) {
                                    case 'NEW_PASSWORD_REQUIRED':
                                        return (
                                            <div>
                                                {this.state.newUser && (
                                                    <section className="form-group">
                                                        <TextInput
                                                            label="Email"
                                                            name="email"
                                                            type="email"
                                                            value={user.email ?? undefined}
                                                            disabled
                                                        />
                                                    </section>
                                                )}
                                                <div
                                                    className={`form-group ${errors.new_password ? 'has-danger' : ''}`}
                                                >
                                                    <label
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            flex: 1,
                                                        }}
                                                    >
                                                        Password
                                                        <Tooltip
                                                            showIcon
                                                            marginLeft="auto"
                                                            hoverText={passwordTooltipText}
                                                        />
                                                    </label>
                                                    <div className="input-group input-group-icon">
                                                        <input
                                                            name="new_password"
                                                            type="password"
                                                            className="form-control form-control-lg"
                                                            value={user.new_password}
                                                            onChange={this.handleChange}
                                                        />
                                                        <span className="input-group-addon">
                                                            <span className="icon icon-lg">
                                                                <i className="fa fa-lock" />
                                                            </span>
                                                        </span>
                                                    </div>
                                                    <InputError error={errors.new_password} />
                                                </div>
                                                <div
                                                    className={`form-group ${
                                                        errors.confirm_password ? 'has-danger' : ''
                                                    }`}
                                                >
                                                    <label>Confirm password</label>
                                                    <div className="input-group input-group-icon">
                                                        <input
                                                            name="confirm_password"
                                                            type="password"
                                                            className="form-control form-control-lg"
                                                            value={user.confirm_password}
                                                            onChange={this.handleChange}
                                                        />
                                                        <span className="input-group-addon">
                                                            <span className="icon icon-lg">
                                                                <i className="fa fa-lock" />
                                                            </span>
                                                        </span>
                                                    </div>
                                                    <InputError error={errors.confirm_password} />
                                                </div>
                                                <div className={classes.formSubmit}>
                                                    <Button
                                                        type="primary"
                                                        onClick={(e) => {
                                                            this.handleUpdatePassword(e);
                                                        }}
                                                        text="Confirm"
                                                        disabled={loading}
                                                        loading={loading}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    case 'SMS_MFA':
                                    case 'SOFTWARE_TOKEN_MFA':
                                        return (
                                            <div>
                                                <div
                                                    className={`form-group ${errors.code ? 'has-danger' : ''}`}
                                                    style={{ position: 'relative' }}
                                                >
                                                    <label>Code</label>
                                                    <div className="input-group input-group-icon">
                                                        <TextInput
                                                            style={{ width: '100%', minWidth: '100%' }}
                                                            name="code"
                                                            placeholder="------"
                                                            type="tel"
                                                            value={user.code || ''}
                                                            autoFocus
                                                            id="mfa_input"
                                                            onChange={this.handleChange}
                                                            errorMessage={errors.code}
                                                        />
                                                    </div>
                                                </div>
                                                <div className={classes.formSubmit}>
                                                    <Button
                                                        type="primary"
                                                        onClick={(e?: React.MouseEvent) =>
                                                            this.handleVerifyCode(e, challengeName)
                                                        }
                                                        text="Submit"
                                                        loading={loading}
                                                        disabled={loading}
                                                        loaderType="in-button blue"
                                                    />
                                                </div>
                                                <span className={classes.hideMobile}>
                                                    <div className={classes.authLink}>
                                                        <p>
                                                            Don&rsquo;t have an account?
                                                            {` `}
                                                            <Link to="/signup">Sign up to Paytron</Link>
                                                        </p>
                                                    </div>
                                                </span>
                                            </div>
                                        );
                                    default:
                                        return (
                                            <>
                                                <div className={`form-group ${errors.email ? 'has-danger' : ''}`}>
                                                    <TextInput
                                                        label={
                                                            this.state.newUser ? 'Confirm your email address' : 'Email'
                                                        }
                                                        name="email"
                                                        type="email"
                                                        placeholder="example@company.com"
                                                        value={user.email ?? undefined}
                                                        onChange={this.handleChange}
                                                        errorMessage={errors.email}
                                                    />
                                                </div>

                                                {this.state.newUser ? (
                                                    <>{this.renderSignUpCodeField()}</>
                                                ) : (
                                                    <div
                                                        className={`form-group ${errors.password ? 'has-danger' : ''}`}
                                                        hidden={this.state.loginState !== 'default'}
                                                    >
                                                        <label>Password</label>
                                                        <Link to="/forgot-pwd" className="right-link">
                                                            Forgot?
                                                        </Link>
                                                        <div className="input-group input-group-icon">
                                                            <input
                                                                name="password"
                                                                type={isShowPassword ? 'text' : 'password'}
                                                                className="form-control form-control-lg"
                                                                placeholder="Your password"
                                                                value={user.password}
                                                                onChange={this.handleChange}
                                                            />
                                                        </div>
                                                        <i
                                                            className={`i-${isShowPassword ? 'unview' : 'view'}`}
                                                            onClick={() => this.handleShowPassword(!isShowPassword)}
                                                        />
                                                        <InputError error={errors.password} />
                                                    </div>
                                                )}

                                                {this.state.newUser ? (
                                                    <div className={`${classes.formSubmit} ${classes.newUserSubmit}`}>
                                                        <p>
                                                            Already have an account?{` `}
                                                            <a href="/signin">Sign in</a>
                                                        </p>
                                                        <Button
                                                            type="primary"
                                                            onClick={this.handleLogin}
                                                            text="Get started"
                                                            loading={loading}
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className={classes.formSubmit}>
                                                            <Button
                                                                type="primary"
                                                                onClick={(e) => this.handleButtonClick(e)}
                                                                style={{ width: '100%' }}
                                                                text={
                                                                    this.state.loginState === 'entry'
                                                                        ? 'Continue'
                                                                        : 'Sign in'
                                                                }
                                                                loading={loading}
                                                                disabled={loading}
                                                            />

                                                            {okta && (
                                                                <Button
                                                                    type="white-condensed"
                                                                    onClick={this.handleOktaExternalSignIn}
                                                                    style={{ width: '100%' }}
                                                                    text="Okta"
                                                                    disabled={loading}
                                                                />
                                                            )}

                                                            {biometrics && biometricOption !== 'Face ID' && (
                                                                <Button
                                                                    type="white"
                                                                    onClick={this.biometricLogin}
                                                                    text={biometricOption ?? undefined}
                                                                    loading={loading}
                                                                    disabled={loading}
                                                                />
                                                            )}
                                                            {biometrics && biometricOption === 'Face ID' && (
                                                                <Button
                                                                    type="white-square"
                                                                    onClick={this.biometricLogin}
                                                                    loaderType="none"
                                                                    icon={<FaceID style={{ height: '20px' }} />}
                                                                    loading={loading}
                                                                    disabled={loading}
                                                                />
                                                            )}
                                                        </div>

                                                        <span className={classes.hideMobile}>
                                                            <div className={classes.dividerText}>
                                                                <p>Or continue with:</p>
                                                            </div>
                                                            <div className="form-group">
                                                                <Button
                                                                    type="white-condensed"
                                                                    onClick={() => {
                                                                        Amplify.configure(awsExports);
                                                                        Auth.federatedSignIn({
                                                                            provider:
                                                                                'LoginWithXero' as any /* eslint-disable-line @typescript-eslint/no-explicit-any -- // INSPECT: Typing is very clear that this should be using customProvider instead of provider, but if it's working I'm not going to change it at this point */,
                                                                            customState: JSON.stringify({
                                                                                type: 'xero_oidc',
                                                                            }),
                                                                        });
                                                                    }}
                                                                    style={{ width: '100%' }}
                                                                    text="Xero"
                                                                    disabled={loading}
                                                                    iconLeft={
                                                                        <img width="25" src={XeroLogo} alt="Xero" />
                                                                    }
                                                                />
                                                            </div>
                                                        </span>

                                                        <span className={classes.hideMobile}>
                                                            <div className={classes.authLink}>
                                                                <p>
                                                                    Don&rsquo;t have an account?
                                                                    {` `}
                                                                    <Link to="/signup">Sign up to Paytron</Link>
                                                                </p>
                                                            </div>
                                                        </span>
                                                    </>
                                                )}
                                            </>
                                        );
                                }
                            })()}
                        </form>
                    </div>
                </div>
            </AuthPageWrapper>
        );
    }
}

type ConfirmSignMfaType = Parameters<typeof Auth.confirmSignIn>[2];
const isConfirmSignMfaType = (challengeName: string | null | undefined): challengeName is ConfirmSignMfaType => {
    if (
        challengeName === 'SMS_MFA' ||
        challengeName === 'SOFTWARE_TOKEN_MFA' ||
        challengeName === null ||
        challengeName === undefined
    ) {
        challengeName satisfies ConfirmSignMfaType;
        ({}) as ConfirmSignMfaType satisfies typeof challengeName;
        return true;
    }
    return false;
};

type ObjectWithCodeProperty = {
    code: string;
};
export const hasCodeProperty = (obj: unknown): obj is ObjectWithCodeProperty => {
    const code: unknown = (obj as Untrusted)?.code;
    if (typeof code === 'string') {
        const check = { code };
        check satisfies ObjectWithCodeProperty;
        ({}) as ObjectWithCodeProperty satisfies typeof check;
        return true;
    }
    return false;
};

export default withRouter(UserSignIn);
